import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TourService } from '../../../../shared/services/tour-service/tour.service';
import {
  DashboardMetrics,
  LeadCountPerStatus,
} from '../../models/dashboard-metrics';

interface LeadOverviewMetric {
  label: string;
  description: string;
  target: number;
  format: 'currency-short' | 'integer';
  tone: 'requested' | 'assigned' | 'unassigned' | 'funded' | 'distributed';
}

interface LeadStatusPieSegment {
  statusLabel: string;
  displayLabel: string;
  count: number;
  percentage: number;
  color: string;
  strokeDasharray: string;
  strokeDashoffset: number;
}

interface LeadStatusPieTooltip {
  label: string;
  count: number;
  percentage: number;
  color: string;
  left: number;
  top: number;
}

@Component({
  selector: 'app-dashboard-leads-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard-leads-status.component.html',
  styleUrl: './dashboard-leads-status.component.scss',
})
export class DashboardLeadsStatusComponent implements OnInit, OnChanges {
  readonly leadOverviewMetrics: LeadOverviewMetric[] = [
    {
      label: 'Total Funding Requested',
      description: 'Total funds requested across the system',
      target: 0,
      format: 'currency-short',
      tone: 'requested',
    },
    {
      label: 'Assigned Funds',
      description:
        'Funds assigned to organizations out of the total requested funds',
      target: 0,
      format: 'currency-short',
      tone: 'assigned',
    },
    {
      label: 'Unassigned Funds',
      description:
        'Funds not yet assigned to any organization out of the total requested funds',
      target: 0,
      format: 'currency-short',
      tone: 'unassigned',
    },
    {
      label: 'Total Funds Approved',
      description: 'Total approved funds recorded across the system',
      target: 0,
      format: 'currency-short',
      tone: 'funded',
    },
    {
      label: 'Funded by You',
      description: 'Approved by your organization',
      target: 0,
      format: 'currency-short',
      tone: 'distributed',
    },
  ];

  private readonly STORAGE_KEY = 'dashboard_expanded_sections';
  private router = inject(Router);
  private tourService = inject(TourService);
  private activeAnimations = new Map<string, number>();
  private celebrationTimeouts = new Map<string, number>();

  @Input() dashboardMetrics: DashboardMetrics | null = null;
  @Output() leadsToastMessage = new EventEmitter<{
    message: string;
    type: 'success' | 'error';
  }>();

  leadStatusCounts: LeadCountPerStatus[] = [];
  pieSegments: LeadStatusPieSegment[] = [];
  pieTooltip: LeadStatusPieTooltip | null = null;
  totalLeads: number | string = '_';
  animatedTotalLeads = '_';
  totalLeadsStatus: number | string = '-';
  animatedMetricValues: Record<string, string> = {};
  showMetricCelebration: Record<string, boolean> = {};
  isExpanded = true;
  showLeadsStatusPopup = false;
  readonly pieChartRadius = 78;
  readonly pieChartCircumference = 2 * Math.PI * this.pieChartRadius;

  ngOnInit(): void {
    this.loadExpandedState();
    this.initializeMetricAnimations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardMetrics'] && this.dashboardMetrics) {
      this.updateFromMetrics();
    }
  }

  private updateFromMetrics(): void {
    if (!this.dashboardMetrics) return;

    // These are leads assigned to the current organization.
    const leadStatusTotal = this.dashboardMetrics.lead_count_per_status.reduce(
      (sum, status) => sum + status.count,
      0,
    );
    const assignedLeadCount =
      this.dashboardMetrics.summary.assigned_lead_count ?? leadStatusTotal;

    this.totalLeads = assignedLeadCount;
    this.totalLeadsStatus = leadStatusTotal;

    this.animateNumber(
      'total-leads',
      assignedLeadCount,
      (value) => (this.animatedTotalLeads = this.formatInteger(value)),
      900,
    );

    // Sort and set lead status counts
    this.leadStatusCounts = [
      ...this.dashboardMetrics.lead_count_per_status,
    ].sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return a.label.localeCompare(b.label);
    });

    this.updatePieSegments();

    // Update metric targets and animate
    this.leadOverviewMetrics[0].target =
      Number(this.dashboardMetrics.summary.total_funding_requested) || 0;
    this.leadOverviewMetrics[1].target =
      Number(this.dashboardMetrics.summary.total_funding_requested_assigned) ||
      0;
    this.leadOverviewMetrics[2].target =
      Number(
        this.dashboardMetrics.summary.total_funding_requested_not_assigned,
      ) || 0;
    this.leadOverviewMetrics[3].target =
      Number(this.dashboardMetrics.summary.total_amount_funded) || 0;
    this.leadOverviewMetrics[4].target =
      Number(this.dashboardMetrics.summary.total_amount_funded_by_my_org) || 0;

    this.leadOverviewMetrics.forEach((metric) => {
      this.animateNumber(
        metric.label,
        metric.target,
        (value) => {
          this.animatedMetricValues[metric.label] = this.formatMetricValue(
            value,
            metric.format,
          );
        },
        1100,
        () => {
          if (metric.tone !== 'unassigned') {
            this.triggerMetricCelebration(metric.label);
          }
        },
      );
    });
  }

  private updatePieSegments(): void {
    const total = Number(this.totalLeadsStatus) || 0;
    if (total <= 0) {
      this.pieSegments = [];
      return;
    }

    const visibleSegmentCount = this.leadStatusCounts.filter(
      (statusCount) => statusCount.count > 0,
    ).length;
    const segmentGap = visibleSegmentCount > 1 ? 2.5 : 0;
    let accumulatedLength = 0;

    this.pieSegments = this.leadStatusCounts
      .filter((statusCount) => statusCount.count > 0)
      .map((statusCount) => {
        const segmentRatio = statusCount.count / total;
        const segmentLength = this.pieChartCircumference * segmentRatio;
        const visibleSegmentLength = Math.max(segmentLength - segmentGap, 0);

        const segment: LeadStatusPieSegment = {
          statusLabel: statusCount.label,
          displayLabel:
            statusCount.label === 'No status' ? 'Not Set' : statusCount.label,
          count: statusCount.count,
          percentage: Math.round(segmentRatio * 100),
          color: this.getStatusColor(statusCount.label),
          strokeDasharray: `${visibleSegmentLength} ${this.pieChartCircumference}`,
          strokeDashoffset: -accumulatedLength,
        };

        accumulatedLength += segmentLength;
        return segment;
      });
  }

  private initializeMetricAnimations(): void {
    this.leadOverviewMetrics.forEach((metric) => {
      this.animatedMetricValues[metric.label] = this.getLoadingMetricValue(
        metric.format,
      );

      this.animateNumber(
        metric.label,
        metric.target,
        (value) => {
          this.animatedMetricValues[metric.label] = this.formatMetricValue(
            value,
            metric.format,
          );
        },
        1100,
        () => {
          if (metric.tone !== 'unassigned') {
            this.triggerMetricCelebration(metric.label);
          }
        },
      );
    });
  }

  private animateNumber(
    key: string,
    target: number,
    onFrame: (value: number) => void,
    duration = 900,
    onComplete?: () => void,
  ): void {
    const previousFrame = this.activeAnimations.get(key);
    if (previousFrame) {
      cancelAnimationFrame(previousFrame);
    }

    if (target <= 0) {
      onFrame(0);
      return;
    }

    const start = performance.now();

    const step = (timestamp: number) => {
      const progress = Math.min((timestamp - start) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = target * easedProgress;

      onFrame(progress === 1 ? target : Math.round(currentValue));

      if (progress < 1) {
        this.activeAnimations.set(key, requestAnimationFrame(step));
      } else {
        this.activeAnimations.delete(key);
        onComplete?.();
      }
    };

    this.activeAnimations.set(key, requestAnimationFrame(step));
  }

  private formatMetricValue(
    value: number,
    format: LeadOverviewMetric['format'],
  ): string {
    if (format === 'currency-short') {
      return this.formatCurrencyShort(value);
    }

    return this.formatInteger(value);
  }

  private formatCurrencyShort(value: number): string {
    if (value >= 1_000_000_000) {
      const amount = value / 1_000_000_000;
      return `$${this.formatDecimal(amount, 2)}B`;
    }

    if (value >= 1_000_000) {
      const amount = value / 1_000_000;
      return `$${this.formatDecimal(amount, 2)}M`;
    }

    if (value >= 1_000) {
      const amount = value / 1_000;
      return `$${this.formatDecimal(amount, 2)}K`;
    }

    return `$${this.formatInteger(value)}`;
  }

  private formatDecimal(value: number, maxDigits = 2): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDigits,
    }).format(value);
  }

  formatExactCurrency(value: number | string | null | undefined): string {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  private formatInteger(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  private getLoadingMetricValue(format: LeadOverviewMetric['format']): string {
    return format === 'currency-short' ? '$0' : '0';
  }

  private triggerMetricCelebration(metricLabel: string): void {
    const existingTimeout = this.celebrationTimeouts.get(metricLabel);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    this.showMetricCelebration[metricLabel] = false;

    setTimeout(() => {
      this.showMetricCelebration[metricLabel] = true;

      const timeoutId = window.setTimeout(() => {
        this.showMetricCelebration[metricLabel] = false;
        this.celebrationTimeouts.delete(metricLabel);
      }, 950);

      this.celebrationTimeouts.set(metricLabel, timeoutId);
    }, 60);
  }

  onLeadsStatusClick(status: string): void {
    const statusItem = this.dashboardMetrics?.lead_count_per_status.find(
      (s) => s.label === status,
    );
    if (statusItem && statusItem.value !== null) {
      this.router.navigate(['/leads/my-leads'], {
        queryParams: { lead_status: statusItem.value },
      });
    } else {
      // For "No status" or null status_value
      this.router.navigate(['/leads/my-leads']);
    }
  }

  onViewMyLeads(): void {
    this.router.navigate(['/leads/my-leads']);
  }

  onLeadsToastMessage(event: { message: string; type: 'success' | 'error' }) {
    this.leadsToastMessage.emit({ message: event.message, type: event.type });
  }

  getStatusColorClass(status: string): string {
    // Normalize status to match CSS class format (lowercase, underscores)
    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return `status-${normalizedStatus}`;
  }

  getStatusColor(status: string): string {
    // Normalize status to handle various formats
    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]/g, '_');

    const colorMap: Record<string, string> = {
      not_defined: '#94a3b8',
      no_status: '#94a3b8',
      not_set: '#94a3b8',
      under_review: '#2563eb',
      further_information_required: '#f59e0b',
      prospective_client: '#14b8a6',
      on_hold: '#8b5cf6',
      declined: '#ef4444',
      not_currently_pursuing: '#64748b',
      active_client: '#22c55e',
      request_lead_contact: '#f97316',
      active_funded: '#06b6d4',
      active_funded_grant: '#84cc16',
      active_technical_assistance: '#ec4899',
    };

    if (colorMap[normalizedStatus]) {
      return colorMap[normalizedStatus];
    }

    // Keep unknown statuses stable and visually distinct without hardcoding every value.
    const fallbackPalette = [
      '#2563eb',
      '#14b8a6',
      '#22c55e',
      '#84cc16',
      '#f59e0b',
      '#f97316',
      '#ef4444',
      '#ec4899',
      '#8b5cf6',
      '#06b6d4',
      '#a16207',
      '#4b5563',
    ];

    let hash = 0;
    for (let i = 0; i < normalizedStatus.length; i += 1) {
      hash = (hash * 31 + normalizedStatus.charCodeAt(i)) >>> 0;
    }

    return fallbackPalette[hash % fallbackPalette.length];
  }

  getPercentage(count: number): number {
    return +this.totalLeadsStatus > 0
      ? Math.round((count / +this.totalLeadsStatus) * 100)
      : 0;
  }

  onPieSegmentHover(event: MouseEvent, segment: LeadStatusPieSegment): void {
    const host = event.currentTarget as SVGCircleElement | null;
    const shell = host?.closest('.status-pie-shell') as HTMLElement | null;

    if (!host || !shell) {
      return;
    }

    const shellRect = shell.getBoundingClientRect();
    const tooltipWidth = 190;
    const tooltipHeight = 78;
    const pointerX = event.clientX - shellRect.left;
    const pointerY = event.clientY - shellRect.top;
    const placeBelowPointer = pointerY < shellRect.height * 0.42;
    const preferredTop = placeBelowPointer
      ? pointerY + 14
      : pointerY - tooltipHeight - 12;

    const left = Math.min(
      Math.max(pointerX + 14, 8),
      shellRect.width - tooltipWidth - 8,
    );
    const top = Math.min(
      Math.max(preferredTop, 8),
      shellRect.height - tooltipHeight - 8,
    );

    this.pieTooltip = {
      label: segment.displayLabel,
      count: segment.count,
      percentage: segment.percentage,
      color: segment.color,
      left,
      top,
    };
  }

  onPieSegmentLeave(): void {
    this.pieTooltip = null;
  }

  toggleExpanded(): void {
    if (this.tourService.isTourMode) {
      this.isExpanded = true;
    } else {
      this.isExpanded = !this.isExpanded;
      this.saveExpandedState();
    }
  }

  private loadExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.leadsStatus === 'boolean') {
          this.isExpanded = parsed.leadsStatus;
        } else if (typeof parsed?.leadStatus === 'boolean') {
          this.isExpanded = parsed.leadStatus;
        }
      } else {
        const legacyStored = localStorage.getItem(
          'dashboard-leads-status-expanded',
        );
        if (legacyStored !== null) {
          this.isExpanded = legacyStored === 'true';
          localStorage.removeItem('dashboard-leads-status-expanded');
          this.saveExpandedState();
        }
      }
    } catch {
      this.isExpanded = true;
    }
  }

  private saveExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      parsed.leadsStatus = this.isExpanded;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore storage errors
    }
  }
}
